/**
 * StorageManager - Gerenciador de persistência local
 * Gerencia high scores, estatísticas e dados do jogo no localStorage
 * 
 * @author Memória dos Imortais Team
 * @version 1.1.0
 */
class StorageManager {
  constructor() {
    // Implementar Singleton pattern
    if (StorageManager.instance) {
      return StorageManager.instance;
    }

    this.storageKey = 'memoria_imortais_data';
    this.version = '1.0';
    
    // Inicializar storage se não existir
    this._initializeStorage();
    
    StorageManager.instance = this;
  }

  /**
   * Inicializa estrutura de dados no localStorage
   * @private
   */
  _initializeStorage() {
    try {
      const existingData = localStorage.getItem(this.storageKey);
      
      if (!existingData) {
        const initialData = {
          version: this.version,
          global: {
            highScore: 0,
            highScoreDate: null,
            highScoreLevel: null
          },
          levels: {},
          statistics: {
            gamesPlayed: 0,
            gamesWon: 0,
            winRate: 0,
            totalPairsFound: 0,
            totalTimePlayedSeconds: 0,
            averageScore: 0
          }
        };
        
        localStorage.setItem(this.storageKey, JSON.stringify(initialData));
      } else {
        // Validar estrutura existente
        this._validateAndMigrateData();
      }
    } catch (error) {
      console.error('Erro ao inicializar storage:', error);
    }
  }

  /**
   * Valida e migra dados antigos se necessário
   * @private
   */
  _validateAndMigrateData() {
    try {
      const data = this._getData();
      let needsUpdate = false;

      // Verificar se tem todos os campos necessários
      if (!data.statistics) {
        data.statistics = {
          gamesPlayed: 0,
          gamesWon: 0,
          winRate: 0,
          totalPairsFound: 0,
          totalTimePlayedSeconds: 0,
          averageScore: 0
        };
        needsUpdate = true;
      }

      if (!data.global) {
        data.global = {
          highScore: 0,
          highScoreDate: null,
          highScoreLevel: null
        };
        needsUpdate = true;
      }

      if (!data.levels) {
        data.levels = {};
        needsUpdate = true;
      }

      if (needsUpdate) {
        this._saveData(data);
      }
    } catch (error) {
      console.error('Erro ao validar dados:', error);
    }
  }

  /**
   * Retorna todos os dados do storage
   * @private
   * @returns {Object} Dados completos
   */
  _getData() {
    try {
      const data = localStorage.getItem(this.storageKey);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error('Erro ao ler dados:', error);
      return null;
    }
  }

  /**
   * Salva dados no storage
   * @private
   * @param {Object} data - Dados a salvar
   */
  _saveData(data) {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(data));
    } catch (error) {
      console.error('Erro ao salvar dados:', error);
    }
  }

  /**
   * Salva resultado de uma partida
   * @param {string} nivelId - ID do nível
   * @param {Object} gameData - Dados da partida
   * @param {number} gameData.score - Pontuação final
   * @param {boolean} gameData.won - Se ganhou
   * @param {number} gameData.pairsFound - Pares encontrados
   * @param {number} gameData.timeSpent - Tempo jogado em segundos
   * @public
   */
  saveGameResult(nivelId, gameData) {
    try {
      const data = this._getData();
      if (!data) return;

      const { score, won, pairsFound, timeSpent } = gameData;

      // Atualizar estatísticas globais
      data.statistics.gamesPlayed++;
      if (won) {
        data.statistics.gamesWon++;
      }
      data.statistics.totalPairsFound += pairsFound;
      data.statistics.totalTimePlayedSeconds += timeSpent;
      data.statistics.winRate = (data.statistics.gamesWon / data.statistics.gamesPlayed) * 100;
      data.statistics.averageScore = Math.round(
        (data.statistics.averageScore * (data.statistics.gamesPlayed - 1) + score) / 
        data.statistics.gamesPlayed
      );

      // Atualizar high score global
      if (score > data.global.highScore) {
        data.global.highScore = score;
        data.global.highScoreDate = new Date().toISOString();
        data.global.highScoreLevel = nivelId;
      }

      // Atualizar dados do nível
      if (!data.levels[nivelId]) {
        data.levels[nivelId] = {
          highScore: 0,
          timesPlayed: 0,
          timesWon: 0,
          completed: false,
          bestTime: null
        };
      }

      const levelData = data.levels[nivelId];
      levelData.timesPlayed++;
      
      if (won) {
        levelData.timesWon++;
        levelData.completed = true;
        
        // Atualizar melhor tempo (menor tempo é melhor)
        if (!levelData.bestTime || timeSpent < levelData.bestTime) {
          levelData.bestTime = timeSpent;
        }
      }

      // Atualizar high score do nível
      if (score > levelData.highScore) {
        levelData.highScore = score;
        levelData.highScoreDate = new Date().toISOString();
      }

      this._saveData(data);
    } catch (error) {
      console.error('Erro ao salvar resultado:', error);
    }
  }

  /**
   * Retorna high score global
   * @returns {number} High score global
   * @public
   */
  getGlobalHighScore() {
    try {
      const data = this._getData();
      return data ? data.global.highScore : 0;
    } catch (error) {
      console.error('Erro ao obter high score global:', error);
      return 0;
    }
  }

  /**
   * Retorna high score de um nível específico
   * @param {string} nivelId - ID do nível
   * @returns {number} High score do nível
   * @public
   */
  getLevelHighScore(nivelId) {
    try {
      const data = this._getData();
      return data && data.levels[nivelId] ? data.levels[nivelId].highScore : 0;
    } catch (error) {
      console.error('Erro ao obter high score do nível:', error);
      return 0;
    }
  }

  /**
   * Retorna estatísticas de um nível
   * @param {string} nivelId - ID do nível
   * @returns {Object} Estatísticas do nível
   * @public
   */
  getLevelStats(nivelId) {
    try {
      const data = this._getData();
      return data && data.levels[nivelId] ? data.levels[nivelId] : {
        highScore: 0,
        timesPlayed: 0,
        timesWon: 0,
        completed: false,
        bestTime: null
      };
    } catch (error) {
      console.error('Erro ao obter stats do nível:', error);
      return {
        highScore: 0,
        timesPlayed: 0,
        timesWon: 0,
        completed: false,
        bestTime: null
      };
    }
  }

  /**
   * Retorna todas as estatísticas globais
   * @returns {Object} Estatísticas gerais
   * @public
   */
  getGlobalStatistics() {
    try {
      const data = this._getData();
      return data ? { ...data.statistics, ...data.global } : {
        gamesPlayed: 0,
        gamesWon: 0,
        winRate: 0,
        totalPairsFound: 0,
        totalTimePlayedSeconds: 0,
        averageScore: 0,
        highScore: 0,
        highScoreDate: null,
        highScoreLevel: null
      };
    } catch (error) {
      console.error('Erro ao obter estatísticas:', error);
      return {
        gamesPlayed: 0,
        gamesWon: 0,
        winRate: 0,
        totalPairsFound: 0,
        totalTimePlayedSeconds: 0,
        averageScore: 0,
        highScore: 0,
        highScoreDate: null,
        highScoreLevel: null
      };
    }
  }

  /**
   * Reseta todos os dados (usado em botão de reset)
   * @public
   */
  resetAllData() {
    try {
      localStorage.removeItem(this.storageKey);
      this._initializeStorage();
      return true;
    } catch (error) {
      console.error('Erro ao resetar dados:', error);
      return false;
    }
  }

  /**
   * Exporta dados como JSON (útil para backup)
   * @returns {string} JSON dos dados
   * @public
   */
  exportData() {
    try {
      const data = this._getData();
      return JSON.stringify(data, null, 2);
    } catch (error) {
      console.error('Erro ao exportar dados:', error);
      return null;
    }
  }

  /**
   * Importa dados de JSON (útil para restaurar backup)
   * @param {string} jsonData - JSON dos dados
   * @returns {boolean} Sucesso ou falha
   * @public
   */
  importData(jsonData) {
    try {
      const data = JSON.parse(jsonData);
      // Validar estrutura básica
      if (data.version && data.statistics && data.levels) {
        this._saveData(data);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Erro ao importar dados:', error);
      return false;
    }
  }
}

// Exportar instância singleton
const storageManager = new StorageManager();
export default storageManager;
